import { JSX, useCallback, useEffect, useState } from 'react'
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { ShopifyAppsCard } from 'components/admin/ShopifyAppsCard'
import { MerchExtensionChannelsCard } from 'components/admin/MerchExtensionChannelsCard'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import {
  DeletionResult,
  MerchRepublishResult,
  MerchShopStatus,
  SHOPIFY_CONNECTION_CONNECTED,
  SHOPIFY_CONNECTION_NEEDS_RECONNECT,
  SHOPIFY_CONNECTION_PCD_NOT_APPROVED,
  SHOPIFY_CONNECTION_UNINSTALLED
} from 'types/api'
import listMerchShopsQuery from 'graphql/listMerchShops.graphql'
import removeMerchShopMutation from 'graphql/removeMerchShop.graphql'
import republishMerchBoardsMutation from 'graphql/republishMerchBoards.graphql'

const relativeAge = (iso: string, nowMs: number): string => {
  const ms = new Date(iso).getTime()
  if (Number.isNaN(ms)) return ''
  const elapsed = Math.max(0, nowMs - ms)
  const hours = Math.floor(elapsed / (60 * 60 * 1000))
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

const STATUS_PILL = 'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset'

const STALE_WEBHOOK_MS = 60 * 60 * 1000

const REPUBLISH_COOLDOWN_MS = 5000

const STALE_WEBHOOK_TITLE = 'No webhook processed in the last hour. On show day that usually means orders are not reaching the pipeline, check the shop status first; Republish only fixes an overlay that is stale, not a shop that is silent.'

const LastWebhookCell = ({ at, nowMs }: { at: string | null, nowMs: number }): JSX.Element => {
  if (at === null || at === '') {
    return <span className='text-amber-400' title={STALE_WEBHOOK_TITLE}>never</span>
  }
  const ms = new Date(at).getTime()
  const text = `${formatLocalDateTime(at)} · ${relativeAge(at, nowMs)}`
  if (Number.isNaN(ms) || nowMs - ms > STALE_WEBHOOK_MS) {
    return <span className='text-amber-400' title={STALE_WEBHOOK_TITLE}>{text}</span>
  }
  return <span>{text}</span>
}

const StatusCell = ({ row, nowMs }: { row: MerchShopStatus, nowMs: number }): JSX.Element => {
  const status = row.connectionStatus ?? null
  if (status === SHOPIFY_CONNECTION_UNINSTALLED) {
    const at = row.uninstalledAt
    const age = at == null ? '' : ` ${relativeAge(at, nowMs)}`
    return (
      <span
        data-testid='merch-shop-status-uninstalled'
        title='The merchant removed the app. Its ledger data (registry row + product counters) was purged immediately, merchant data leaves when the merchant does '
        className={`${STATUS_PILL} bg-white/5 text-dm-text-accent ring-white/10`}
      >
        uninstalled{age}
      </span>
    )
  }
  if (status === SHOPIFY_CONNECTION_PCD_NOT_APPROVED) {
    return (
      <span
        data-testid='merch-shop-status-pcd'
        title='Shopify has not approved this app for protected customer data, so the order webhooks cannot be created. TERMINAL: fixed in the Partner Dashboard, never by retrying here.'
        className={`${STATUS_PILL} bg-red-500/15 text-red-300 ring-red-500/30`}
      >
        not approved for protected data
      </span>
    )
  }
  if (status === SHOPIFY_CONNECTION_NEEDS_RECONNECT) {
    return (
      <span
        data-testid='merch-shop-status-needs-reconnect'
        title='The stored authorization no longer refreshes, the streamer can fix this themselves from their Connections page.'
        className={`${STATUS_PILL} bg-amber-500/15 text-amber-400 ring-amber-500/30`}
      >
        needs reconnect
      </span>
    )
  }
  if (status === SHOPIFY_CONNECTION_CONNECTED) {
    return (
      <span className={`${STATUS_PILL} bg-emerald-500/15 text-emerald-400 ring-emerald-500/30`}>
        connected
      </span>
    )
  }
  return (
    <span
      title='No connection state stamped on this row'
      className='text-dm-text-accent'
    >
      —
    </span>
  )
}

const SourceBadge = ({ source }: { source: string | null | undefined }): JSX.Element => {
  if (source === 'oauth') {
    return (
      <span
        title='Installed by the merchant clicking the install link (authorization code grant)'
        className={`${STATUS_PILL} bg-brand/15 text-brand ring-brand/30`}
      >
        oauth
      </span>
    )
  }
  return <span className='text-dm-text-accent'>—</span>
}

const RegistryBody = ({
  shops,
  loading,
  error,
  nowMs,
  removingShop,
  republishingShop,
  cooldownShop,
  onRemove,
  onRepublish,
  onRetry
}: {
  shops: MerchShopStatus[] | null
  loading: boolean
  error: string | null
  nowMs: number
  removingShop: string | null
  republishingShop: string | null
  cooldownShop: string | null
  onRemove: (row: MerchShopStatus) => void
  onRepublish: (row: MerchShopStatus) => void
  onRetry: () => void
}): JSX.Element => {
  if (error !== null) {
    return (
      <div className='mt-3 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
        <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
        <span className='flex-1'>{error}</span>
        <button
          onClick={onRetry}
          className='shrink-0 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20'
        >
          Retry
        </button>
      </div>
    )
  }
  if (loading || shops === null) {
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  if (shops.length === 0) {
    return (
      <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary'>
        No shops registered yet. Send a client their install link from the
        Shopify apps card above.
      </p>
    )
  }
  return (
    <div className='mt-3 overflow-x-auto'>
      <table className='w-full min-w-[1320px] text-left text-[11px]'>
        <thead>
          <tr className='border-b border-white/10 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
            <th className='py-1.5 pr-3'>Shop</th>
            <th className='py-1.5 pr-3'>App key</th>
            <th className='py-1.5 pr-3' title='Stamped on the ledger row by the OAuth callback and the uninstall consumer. Never read from the credential table'>Status</th>
            <th className='py-1.5 pr-3' title='How the credential was obtained'>Source</th>
            <th className='py-1.5 pr-3' title='The StagePilot account this shop is bound to shown by email, resolved at read time (never stored); falls back to the raw account id when the lookup misses'>Bound account</th>
            <th className='py-1.5 pr-3' title="The Twitch extension broadcast target, the bound account's Twitch user id, stamped at bind from its Twitch connection. Empty = no extension leg for this shop yet (the account had no Twitch connection when it installed)">Broadcaster</th>
            <th className='py-1.5 pr-3' title='When the pipeline last processed a webhook for this shop, with its age. Amber past an hour (or never) a show-day staleness hint, not an alarm'>Last webhook</th>
            <th className='py-1.5 pr-3' title='The shop ledger&apos;s monotonic version every processed order/refund bumps it'>Version</th>
            <th className='py-1.5 pr-3'>Products</th>
            <th className='py-1.5' />
          </tr>
        </thead>
        <tbody>
          {shops.map((row) => (
            <tr key={row.shop} className='border-b border-white/5 text-dm-text-secondary'>
              <td className='py-1.5 pr-3 font-mono text-dm-text-primary'>{row.shop}</td>
              <td className='py-1.5 pr-3 font-mono'>{row.appKey}</td>
              <td className='py-1.5 pr-3'><StatusCell row={row} nowMs={nowMs} /></td>
              <td className='py-1.5 pr-3'><SourceBadge source={row.source} /></td>
              <td className='max-w-[14rem] truncate py-1.5 pr-3' title={row.accountId}>
                {row.ownerEmail ?? <span className='font-mono'>{row.accountId}</span>}
              </td>
              <td className='py-1.5 pr-3 font-mono'>{row.broadcasterId ?? '—'}</td>
              <td className='py-1.5 pr-3'>
                <LastWebhookCell at={row.lastWebhookAt} nowMs={nowMs} />
              </td>
              <td className='py-1.5 pr-3 tabular-nums'>{row.version}</td>
              <td className='py-1.5 pr-3 tabular-nums'>{row.productCount}</td>
              <td className='py-1.5'>
                <div className='flex items-center gap-1.5'>
                  <button
                    type='button'
                    onClick={() => onRepublish(row)}
                    disabled={republishingShop !== null || removingShop !== null || cooldownShop === row.shop}
                    data-testid='merch-shop-republish'
                    title="Recompose and republish this shop's boards to both overlay legs, the fix when an overlay looks stale mid-show"
                    className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    {republishingShop === row.shop ? <LoadingSpinner /> : <SignalIcon className='size-3.5' aria-hidden='true' />}
                    Republish
                  </button>
                  <button
                    type='button'
                    onClick={() => onRemove(row)}
                    disabled={removingShop !== null || republishingShop !== null}
                    title="Remove this shop (webhooks deleted best-effort; the shop's ledger data, registry row and sales counters purged immediately)"
                    className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-[11px] font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
                  >
                    {removingShop === row.shop ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
                    Remove
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const MerchAdminSection = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [shops, setShops] = useState<MerchShopStatus[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const [removingShop, setRemovingShop] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)

  const [republishingShop, setRepublishingShop] = useState<string | null>(null)
  const [republishError, setRepublishError] = useState<string | null>(null)
  const [republishResult, setRepublishResult] = useState<MerchRepublishResult | null>(null)
  const [cooldownShop, setCooldownShop] = useState<string | null>(null)
  useEffect(() => {
    if (cooldownShop === null) return
    const id = globalThis.setTimeout(() => setCooldownShop(null), REPUBLISH_COOLDOWN_MS)
    return () => globalThis.clearTimeout(id)
  }, [cooldownShop])

  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const id = globalThis.setInterval(() => setNowMs(Date.now()), 60_000)
    return () => globalThis.clearInterval(id)
  }, [])

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setListError(null)
    try {
      const { shops: fetched } = await graphqlRequest<{ shops: MerchShopStatus[] }>(
        listMerchShopsQuery, {}, auth0Token
      )
      setShops([...fetched].sort((a, b) => a.shop.localeCompare(b.shop)))
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load the shop registry')
    } finally {
      setLoading(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void load()
  }, [load])

  const handleRemove = async (row: MerchShopStatus): Promise<void> => {
    if (removingShop !== null) return
    const ok = globalThis.confirm(
      `Remove ${row.shop}? Its webhooks are deleted (best-effort), the stored admin token is destroyed, its ledger data (registry row + sales counters) is purged immediately, and boards bound to it stop updating.`
    )
    if (!ok) return
    setRemovingShop(row.shop)
    setRemoveError(null)
    try {
      await graphqlRequest<{ result: DeletionResult }>(
        removeMerchShopMutation,
        { shop: row.shop, accountId: row.accountId },
        auth0Token
      )
      setShops((prev) => prev === null ? prev : prev.filter((s) => s.shop !== row.shop))
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove the shop')
    } finally {
      setRemovingShop(null)
    }
  }

  const handleRepublish = async (row: MerchShopStatus): Promise<void> => {
    if (republishingShop !== null || removingShop !== null || cooldownShop === row.shop) return
    setRepublishingShop(row.shop)
    setRepublishError(null)
    setRepublishResult(null)
    try {
      const { result } = await graphqlRequest<{ result: MerchRepublishResult }>(
        republishMerchBoardsMutation, { shop: row.shop }, auth0Token
      )
      setRepublishResult(result)
      setCooldownShop(row.shop)
      void load()
    } catch (err) {
      setRepublishError(err instanceof Error ? err.message : 'Failed to republish the boards for this shop')
    } finally {
      setRepublishingShop(null)
    }
  }

  return (
    <section data-testid='merch-admin-section' className='mt-10'>
      <img src='/images/shopify-logo-color-dark-bg.png' alt='Shopify' className='h-6 pl-2 w-auto' />
      <ShopifyAppsCard auth0Token={auth0Token} />
      <div
        data-testid='merch-shop-registry-card'
        className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
      >
        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-center gap-2'>
            <h3 className='text-base font-semibold text-dm-text-primary'>Shop Registry</h3>
            {shops !== null && (
              <span className='inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-brand/15 px-2 text-xs font-semibold text-brand'>
                {shops.length}
              </span>
            )}
          </div>
          <button
            onClick={() => { void load() }}
            disabled={loading}
            title='Refresh the shop registry'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <ArrowPathIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden='true' />
            Refresh
          </button>
        </div>
        <p className='mt-1.5 text-xs text-dm-text-secondary'>
          Shopify shops feeding the merch pipeline: which account each is
          bound to, how it was connected, and its pipeline health (last
          webhook, ledger version, tracked products). Rows appear here on
          their own the moment a client finishes an install.
        </p>
        <p className='mt-1.5 text-[11px] text-dm-text-accent'>
          Extension-publish failures emit CloudWatch EMF{' '}
          <span className='font-mono'>StreamTools / PublishFailures</span> with{' '}
          <span className='font-mono'>Operation=&quot;merch-extension&quot;</span> alarm on that
          dimension. Republish recomposes a shop&apos;s boards and pushes them to
          both legs; it bumps the ledger version first, or the overlays would
          drop the push as not newer.
        </p>
        {removeError !== null && (
          <div className='mt-3'>
            <ErrorInline onDismiss={() => setRemoveError(null)}>{removeError}</ErrorInline>
          </div>
        )}
        {republishError !== null && (
          <div className='mt-3'>
            <ErrorInline onDismiss={() => setRepublishError(null)}>{republishError}</ErrorInline>
          </div>
        )}
        {republishResult !== null && (
          <p data-testid='merch-republish-result' className='mt-3 text-xs text-emerald-300'>
            {republishResult.shop} republished {republishResult.boardsPublished} board(s) · v{republishResult.version}
            {republishResult.extensionAttempted ? ' · extension leg attempted' : ' · no broadcaster bound, overlay leg only'}
          </p>
        )}
        <RegistryBody
          shops={shops}
          loading={loading}
          error={listError}
          nowMs={nowMs}
          removingShop={removingShop}
          republishingShop={republishingShop}
          cooldownShop={cooldownShop}
          onRemove={(row) => { void handleRemove(row) }}
          onRepublish={(row) => { void handleRepublish(row) }}
          onRetry={() => { void load() }}
        />
      </div>
      <MerchExtensionChannelsCard auth0Token={auth0Token} />
    </section>
  )
}
